import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';

import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Upload,
  FileText,
  Download,
  Eye,
  RefreshCw,
  Calendar,
  Users,
  DollarSign,
  FileCheck,
  AlertCircle,
  CheckCircle,
  Trash2,
  Settings
} from 'lucide-react';

const HoleritesPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const allowedTabs = ['upload', 'history'];
  const [activeTab, setActiveTab] = useState('upload');
  const [loading, setLoading] = useState(false);
  
  // Estados para upload
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Estados para histórico
  const [processedFiles, setProcessedFiles] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Estados para estatísticas
  const [stats, setStats] = useState({
    totalProcessed: 0,
    currentMonth: 0,
    totalEmployees: 0,
    pendingDownloads: 0
  });

  const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    if (user) {
      loadProcessedFiles();
      loadStats();
    }
  }, [user]);

  const loadProcessedFiles = async () => {
    try {
      setLoadingHistory(true);
      const response = await axios.get(`${API}/api/holerites/history`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setProcessedFiles(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      toast.error('Erro ao carregar histórico de holerites');
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/api/holerites/stats`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setStats(response.data || stats);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tipo de arquivo
    const allowedTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de arquivo não suportado. Use arquivos Excel (.xls ou .xlsx)');
      return;
    }

    // Validar tamanho (máx 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 50MB.');
      return;
    }

    setSelectedFile(file);
    toast.success(`Arquivo "${file.name}" selecionado com sucesso!`);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Selecione um arquivo primeiro');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('month', new Date().getMonth() + 1);
      formData.append('year', new Date().getFullYear());

      const response = await axios.post(`${API}/api/holerites/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      toast.success(`Arquivo processado com sucesso! ${response.data.processed_count} holerites gerados.`);
      
      // Resetar form
      setSelectedFile(null);
      setUploadProgress(0);
      
      // Recarregar dados
      loadProcessedFiles();
      loadStats();
      
      // Ir para aba de histórico
      setActiveTab('history');

    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      const errorMessage = error.response?.data?.detail || 'Erro ao processar arquivo';
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const downloadAllPDFs = async (processId) => {
    try {
      const response = await axios.get(`${API}/api/holerites/${processId}/download-all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });

      // Criar URL para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `holerites_${processId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Download iniciado com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      toast.error('Erro ao fazer download dos holerites');
    }
  };

  const downloadSinglePDF = async (processId, employeeId, employeeName) => {
    try {
      const response = await axios.get(`${API}/api/holerites/${processId}/employee/${employeeId}/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `holerite_${employeeName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Download do holerite de ${employeeName} iniciado!`);
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      toast.error('Erro ao fazer download do holerite');
    }
  };

  const deleteProcess = async (processId) => {
    if (!window.confirm('Tem certeza que deseja excluir este processamento?')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/holerites/${processId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success('Processamento excluído com sucesso!');
      loadProcessedFiles();
      loadStats();
    } catch (error) {
      console.error('Erro ao excluir processamento:', error);
      toast.error('Erro ao excluir processamento');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-3 bg-gradient-to-r from-blue-500 to-green-500 rounded-xl">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                Geração de Holerites
              </h1>
              <p className="text-gray-600">Processamento automático de recibos de pagamento</p>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Total Processados</p>
                  <p className="text-2xl font-bold text-blue-700">{stats.totalProcessed}</p>
                </div>
                <FileCheck className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Mês Atual</p>
                  <p className="text-2xl font-bold text-green-700">{stats.currentMonth}</p>
                </div>
                <Calendar className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-purple-200 bg-purple-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Funcionários</p>
                  <p className="text-2xl font-bold text-purple-700">{stats.totalEmployees}</p>
                </div>
                <Users className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-orange-200 bg-orange-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600 font-medium">Downloads Pendentes</p>
                  <p className="text-2xl font-bold text-orange-700">{stats.pendingDownloads}</p>
                </div>
                <Download className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Card className="shadow-xl border-0">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-t-lg">
            <CardTitle className="flex items-center space-x-2">
              <FileText className="h-5 w-5" />
              <span>Gestão de Holerites</span>
            </CardTitle>
            <CardDescription className="text-blue-100">
              Upload e processamento de arquivos Excel do contador
            </CardDescription>
          </CardHeader>

          <CardContent className="p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                {allowedTabs.includes('upload') && (
                  <TabsTrigger value="upload" className="flex items-center space-x-2">
                    <Upload className="h-4 w-4" />
                    <span>Upload Arquivo</span>
                  </TabsTrigger>
                )}
                {allowedTabs.includes('history') && (
                  <TabsTrigger value="history" className="flex items-center space-x-2">
                    <FileText className="h-4 w-4" />
                    <span>Histórico</span>
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Upload Tab */}
              {allowedTabs.includes('upload') && (
              <TabsContent value="upload" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Upload de Arquivo Excel</CardTitle>
                    <CardDescription>
                      Selecione o arquivo Excel enviado pelo contador com os dados de todos os funcionários
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* File Upload Area */}
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                      <input
                        type="file"
                        id="file-upload"
                        accept=".xls,.xlsx"
                        onChange={handleFileSelect}
                        disabled={uploading}
                        className="hidden"
                      />
                      <label htmlFor="file-upload" className="cursor-pointer">
                        {uploading ? (
                          <RefreshCw className="h-12 w-12 text-blue-500 animate-spin mx-auto mb-4" />
                        ) : (
                          <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        )}
                        
                        {selectedFile ? (
                          <div>
                            <p className="text-lg font-medium text-green-600 mb-2">
                              ✓ {selectedFile.name}
                            </p>
                            <p className="text-sm text-gray-500">
                              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        ) : (
                          <div>
                            <p className="text-lg font-medium text-gray-600 mb-2">
                              {uploading ? 'Processando arquivo...' : 'Clique para selecionar arquivo'}
                            </p>
                            <p className="text-sm text-gray-400">
                              Formatos aceitos: .xls, .xlsx (máx. 50MB)
                            </p>
                          </div>
                        )}
                      </label>
                    </div>

                    {/* Upload Progress */}
                    {uploading && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Progresso do upload</span>
                          <span>{uploadProgress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {/* Upload Button */}
                    <div className="flex justify-center">
                      <Button
                        onClick={handleUpload}
                        disabled={!selectedFile || uploading}
                        className="px-8 py-3 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700"
                        size="lg"
                      >
                        {uploading ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="h-4 w-4 mr-2" />
                        )}
                        {uploading ? 'Processando...' : 'Processar Holerites'}
                      </Button>
                    </div>

                    {/* Instructions */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-800 mb-2 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-2" />
                        Instruções de Uso
                      </h4>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>• O arquivo deve ser enviado pelo contador no formato Excel (.xls ou .xlsx)</li>
                        <li>• Cada linha deve representar um funcionário com todos os dados do holerite</li>
                        <li>• O sistema gerará automaticamente um PDF individual para cada funcionário</li>
                        <li>• Após o processamento, você poderá baixar os PDFs individualmente ou em lote</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              )}

              {/* History Tab */}
              {allowedTabs.includes('history') && (
              <TabsContent value="history" className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium">Histórico de Processamentos</h3>
                  <Button
                    variant="outline"
                    onClick={() => { loadProcessedFiles(); loadStats(); }}
                    disabled={loadingHistory}
                  >
                    {loadingHistory ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Atualizar
                  </Button>
                </div>

                {loadingHistory ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-blue-500 mr-2" />
                    <span>Carregando histórico...</span>
                  </div>
                ) : processedFiles.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>Nenhum processamento encontrado</p>
                    <p className="text-sm">Faça o upload do primeiro arquivo Excel</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {processedFiles.map((process) => (
                      <Card key={process.id} className="border border-gray-200">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <FileCheck className="h-5 w-5 text-green-500" />
                                <h4 className="font-medium">{process.filename}</h4>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  process.status === 'completed' ? 'bg-green-100 text-green-700' :
                                  process.status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {process.status === 'completed' ? 'Concluído' :
                                   process.status === 'processing' ? 'Processando' : 'Erro'}
                                </span>
                              </div>
                              <div className="text-sm text-gray-500 space-y-1">
                                <p>📅 {new Date(process.created_at).toLocaleDateString('pt-BR')} - {process.month}/{process.year}</p>
                                <p>👥 {process.processed_count} funcionários processados</p>
                                <p>💼 Processado por: {process.processed_by}</p>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => downloadAllPDFs(process.id)}
                                className="flex items-center space-x-1"
                              >
                                <Download className="h-4 w-4" />
                                <span>Baixar Todos</span>
                              </Button>
                              
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => deleteProcess(process.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Employee List */}
                          {process.employees && process.employees.length > 0 && (
                            <div className="mt-4 pt-4 border-t">
                              <p className="text-sm font-medium text-gray-700 mb-2">Funcionários:</p>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {process.employees.map((employee, index) => (
                                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                    <span className="text-sm text-gray-700">{employee.name}</span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => downloadSinglePDF(process.id, employee.id, employee.name)}
                                      className="text-blue-600 hover:text-blue-700"
                                    >
                                      <Download className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
              )}
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HoleritesPage;